"use client";

import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import * as React from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@beweegbaas/ui/components/ui/input-group";
import { cn, cnState } from "@beweegbaas/ui/lib/utils";
import { IconCheck, IconChevronDown, IconX } from "@tabler/icons-react";

const Combobox = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cnState("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <IconChevronDown className="text-muted-foreground pointer-events-none size-4" />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="ghost" size="icon-xs" />}
      className={className}
      {...props}
    >
      <IconX className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  );
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean;
  showClear?: boolean;
}) {
  return (
    <InputGroup className={cn("w-auto", className)}>
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            render={<ComboboxTrigger />}
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden
              data-pressed:bg-transparent"
            disabled={disabled}
          />
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  collisionPadding,
  collisionBoundary,
  sticky,
  positionerClassName,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    | "side"
    | "align"
    | "sideOffset"
    | "alignOffset"
    | "anchor"
    | "collisionPadding"
    | "collisionBoundary"
    | "sticky"
  > & {
    positionerClassName?: string;
  }) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary}
        sticky={sticky}
        className={cn("isolate z-50", positionerClassName)}
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cnState(
            `bg-popover text-popover-foreground data-open:animate-in
            data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0
            data-closed:zoom-out-95 data-open:zoom-in-95
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2 ring-foreground/10
            *:data-[slot=input-group]:bg-input/30
            *:data-[slot=input-group]:border-input/30 dark
            group/combobox-content relative max-h-[min(var(--available-height),18rem)]
            w-(--anchor-width) max-w-(--available-width) min-w-36
            min-w-[calc(var(--anchor-width)+--spacing(7))]
            origin-(--transform-origin) overflow-hidden rounded-md shadow-md
            ring-1 duration-100 data-[chips=true]:min-w-(--anchor-width)
            *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0
            *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none`,
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cnState(
        `no-scrollbar
        max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))]
        scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0`,
        className,
      )}
      {...props}
    />
  );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cnState(
        `data-highlighted:bg-accent data-highlighted:text-accent-foreground
        not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground
        relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5
        pr-8 pl-2 text-sm outline-hidden select-none
        data-[disabled]:pointer-events-none data-[disabled]:opacity-50
        [&_svg]:pointer-events-none [&_svg]:shrink-0
        [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span
            className="pointer-events-none absolute right-2 flex size-4
              items-center justify-center"
          />
        }
      >
        <IconCheck className="pointer-events-none" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={className} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cnState("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  // Base UI keeps Empty mounted as a live region and only renders children when
  // the list is empty — never hide it with display:none; collapse padding instead.
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cnState(
        `text-muted-foreground flex w-full justify-center px-3 py-2
        text-center text-sm empty:p-0`,
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cnState("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function ComboboxChips({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cnState(
        `dark:bg-input/30 border-input focus-within:border-ring
        focus-within:ring-ring/50 has-aria-invalid:ring-destructive/20
        dark:has-aria-invalid:ring-destructive/40
        has-aria-invalid:border-destructive
        dark:has-aria-invalid:border-destructive/50 flex min-h-9 flex-wrap
        items-center gap-1.5 rounded-md border bg-transparent bg-clip-padding
        px-2.5 py-1.5 text-sm shadow-xs transition-[color,box-shadow]
        focus-within:ring-[3px] has-aria-invalid:ring-[3px]
        has-data-[slot=combobox-chip]:px-1.5`,
        className,
      )}
      {...props}
    />
  );
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cnState(
        `bg-muted text-foreground flex h-[calc(--spacing(5.5))] w-fit
        items-center justify-center gap-1 rounded-sm px-1.5 text-xs font-medium
        whitespace-nowrap has-disabled:pointer-events-none
        has-disabled:cursor-not-allowed has-disabled:opacity-50
        has-data-[slot=combobox-chip-remove]:pr-0`,
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant="ghost" size="icon-xs" />}
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <IconX className="pointer-events-none" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChipsInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cnState("min-w-16 flex-1 outline-none", className)}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

const useComboboxFilter = ComboboxPrimitive.useFilter;
const useComboboxFilteredItems = ComboboxPrimitive.useFilteredItems;

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxPrimitive,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useComboboxFilter,
  useComboboxFilteredItems,
};
